/* eslint-disable camelcase */
/**
 * Root interface for the x-auth module
 */

import { DafAuthConfig, AuthConfig, AuthToken, DafAuthToken, DafGrantType } from '@nab/x-auth-core';
import { AppLoaderConfig, App, XV2App } from '@nab/x-types';

/**
 * @todo new interface with clientId & scopes mandatory
 */
export interface ApigeeKerberosAuthConfig extends DafAuthConfig {
    appId: string;
    proxyName: string;
    authEndpoint?: string;
}

export interface LinkedAppAuthInitConfig<A extends App = App, AUTH extends AuthConfig = AuthConfig> {
    linkedAppConfig: Omit<AppLoaderConfig<A, AUTH>, 'hostElement'>;
    successEventNames: string[]; // consider changing this array, in case multiple success and failure events are provided
    failureEventNames: string[];
    onSuccess?: () => void;
    onFailure?: (e: any) => void;
    getHostElement: () => Promise<HTMLElement>;
}

export enum LinkedAppAuthError {
    LOAD_LINKED_APP_ERROR = 'Cannot embed linked auth miniapp',
    UNLOAD_LINKED_APP_ERROR = 'Cannot unload linked auth miniapp',
    INVALID_TOKEN = 'Token cannot be retrieved',
    FAILURE_AUTH_PROCESS = 'Authentication process failed',
    MISSED_SHELL_ACTIONS = 'Failed to authenticate using the linked miniapp. The actions are missing.',
    MISSED_GET_HOST_ELEMENT = 'Invalid getHostElement provided for linked app. getHostEment must returns a native DOM element'
}

/**
 * `requestTokenOnLoad` will always be false as mobile loads a miniapp in webview with a prefetched auth token
 */
export interface MobileAuthConfig extends AuthConfig {
    appMetaData: XV2App;
}

export interface MobileAuthToken<C extends MobileAuthConfig = MobileAuthConfig> extends AuthToken<C> {
    issuedAt?: number; // Time of issue
    expires?: number; // Expiry time
}

export interface GenerateForeignOAuthAuthorizationRequestConfig {
    csid: string;
    consentId: string;
    responseType?: string;
    scope: string;
    claims?: string;
}

export interface RequestForeignOAuthTokenConfig {
    consentId: string;
}

export interface DafAuthorizationAuthConfig<P extends DafAuthConfig = DafAuthConfig> extends DafAuthConfig {
    issuer: string;
    orgCode: string;
    scheme: string;
    authorizationFlowConfig?: GenerateForeignOAuthAuthorizationRequestConfig;
    tokenFlowConfig?: RequestForeignOAuthTokenConfig;
}

export interface DafAuthorizationAuthStrategyReturn extends DafAuthToken<DafAuthorizationAuthConfig> {
    cdrArrangementId?: string;
}
export interface GenerateForeignOAuthAuthorizationRequest {
    request_id: string;
    request_url: string;
    request_params: string;
}

export interface GenerateForeignOAuthAuthorization {
    requestId: string;
    requestUrl: string;
    requestParams: string;
}

export interface RequestForeignOAuthToken {
    consentId: string;
    accessToken: string;
    expiresIn: number;
    scope: string;
    idToken: string;
    cdrArrangementId: string;
}

export interface RequestForeignOAuthTokenPayload {
    consent_id: string;
    request_id: string;
    code?: string;
    id_token?: string;
    state?: string;
    error?: string;
    error_description?: string;
    response?: string;
}

export interface RequestForeignOAuthTokenResponse {
    error?: string;
    error_description: any;
    consent_id?: string;
    access_token: string;
    expires_in: number;
    scope: string;
    id_token: string;
    cdr_arrangement_id: string;
}

export enum GenerateForeignAuthorizationErrorKeys {
    InvalidRequest = 'DA-420',
    InvalidClient = 'DA-618',
    ServerError = 'DA-671',
    InvalidToken = 'DA-410',
    AccessDenied = 'DA-438',
    ClientIsDisabled = 'DA-439',
    UnableToPerformOperation = 'DA-500',
    UnableUpstreamService = 'DA-502',
    UnknownOrg = 'DA-685'
}
export enum ForeignOauthTokenErrorKeys {
    InvalidRequest = 'DA-420',
    InvalidRequestId = 'DA-649',
    InvalidIdToken = 'DA-670',
    ServerError = 'DA-671',
    InvalidResponseJwt = 'DA-672',
    InvalidToken = 'DA-410',
    AccessDenied = 'DA-438',
    ClientIsDisabled = 'DA-439',
    UnableToPerformOperation = 'DA-500',
    UnableUpstreamService = 'DA-502',
    validate_failed = 'validate_failed'
}
export interface ExpectErrorResponse {
    error: string;
    errorMessage: string;
}

export interface ErrorMessageTestcase {
    errorId: string;
    message: string;
    expectResponse: ExpectErrorResponse;
}

export enum TOKEN_TYPE {
    FOREIGN = 'foreign'
}

export enum DafEnvironments {
    LOCAL = 'LOCAL',
    JENKINS = 'JENKINS',
    DOCKER = 'DOCKER',
    SIT1 = 'SIT1',
    SIT4 = 'SIT4',
    SIT5 = 'SIT5',
    PPTE = 'PPTE',
    PROD = 'PROD',
    NFT = 'NFT'
}

export interface DafAuthApiError {
    error: string;
    error_description: string;
    httpCode: string | number;
}

export interface DafAuthEnvironmentConfig {
    kongCustomerGatewayBaseUrl: string;
    kongStaffGatewayBaseUrl: string;
    axwayBaseUrl: string;
}

export interface DafAuthApiErrorDetail {
    field?: string;
    value?: string;
    location?: 'query' | 'path' | 'body' | 'header';
    issue: string;
}

export interface DafAuthApiErrorResponse<D = any, U = any> {
    errorId: string;
    message: string;
    informationLink?: string;
    details?: D;
    userInterface?: U;
}

export interface LegacyDafApiErrorResponse {
    error: string;
    error_description: string;
}

export interface DafAuthPersist<T> {
    get(): T;
    set(value: T): void;
    clear(): void;
}

export interface DafAuthGeneratedCodes {
    codeVerifier: string;
    state: string;
    codeChallenge: string;
}

export interface DafAuthDisplayStrategy {
    authorize(url: string): Promise<DafEmployeeSsoState | void>;
    onAuthorized(authState: DafEmployeeSsoState): Promise<void>;
    getPersist(): DafAuthPersist<DafAuthGeneratedCodes>;
    strategyName: string;
    shouldBreak(authState: DafEmployeeSsoState | void): boolean;
    toString(): string;
}
export type EmployeeSsoResponseType = 'code' | 'code id_token';

export interface DafEmployeeSsoState {
    code?: string;
    state: string;
    error?: string;
}

export interface DafEmployeeSsoConfig {
    clientId: string;
    scope: string;
    responseType: EmployeeSsoResponseType;
    environment: DafEnvironments;
    displayStrategy?: DafAuthDisplayStrategy;
    initialState?: DafEmployeeSsoState;
    redirectUri?: string;
}

export interface DafAuthSuccessResponse<D> {
    data: D;
}

export interface DafProcessAuthResponse {
    code?: string;
    state: string;
    error?: string;
}

export type DafEmployeeSsoAuthenticateResponse = DafAuthSuccessResponse<{
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    scope?: string;
    idToken?: string;
    lastLoginTime?: string;
    idleTimeout?: number;
    bearerToken?: string;
    issuedTokenType?: string;
}>;

export interface IdmDafRequestOAuthAuthorizationConfig {
    client_id: string;
    response_type?: 'code' | 'code id_token';
    scope: string;
    environment?: DafEnvironments;
    redirect_uri: string;
}

export type IdmDafRequestOAuthAuthorizationResponse = DafAuthSuccessResponse<{
    url: string;
    codeVerifier: string;
    codeChallenge: string;
    state: string;
}>;

export interface IdmDafRequestOAuthZGeneratedValues {
    code_verifier: string;
    state: string;
    code_challenge: string;
}

export interface DafAuthUserDevicesParams {
    tenant: string;
    userType: 'Internal' | 'External';
    usid: string;
}

export interface IdmDafRequestOAuthTokenConfig {
    environment?: DafEnvironments;
    client_id: string;
    scope?: string;
    grant_type: DafGrantType;
    code?: string;
    code_verifier?: string;
    redirect_uri?: string;
    client_secret?: string;
    client_assertion_type?: string;
    client_assertion?: string;
}

export interface DafAuthApiResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    scope?: string;
    id_token?: string;
    lastLoginTime?: string;
    idleTimeout?: number;
    bearerToken?: string;
    issued_token_type?: string;
}

export interface DafApiErrorResponse extends DafAuthApiError {
    error_description: any;
    error: 'invalid_request' | 'invalid_client' | 'invalid_grant' | 'unauthorized_client' | 'unsupported_grant_type' | 'invalid_scope';
}

export type IdmDafRequestOAuthTokenResponse = DafAuthSuccessResponse<{
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    scope?: string;
    idToken?: string;
    lastLoginTime?: string;
    idleTimeout?: number;
    bearerToken?: string;
    issuedTokenType?: string;
}>;

export interface IDafEmployeeSsoAuthStrategyConfig {
    clientId: string;
    responseType?: EmployeeSsoResponseType;
    displayStrategy: DafAuthDisplayStrategy;
    redirectUri?: string;
    isSeed?: boolean;
}
export interface IDafEmployeeSsoAuthToken extends DafAuthToken<DafAuthConfig> {
    scope?: string;
    idToken?: string;
    lastLoginTime?: string;
    issuedTokenType?: string;
}
